import { create } from 'zustand'

interface WebSocketMessage {
  type: string
  data: any
  timestamp: string
}

interface WebSocketState {
  isConnected: boolean
  lastMessage: WebSocketMessage | null
  connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'error'
  
  // Actions
  connect: () => void
  disconnect: () => void
  subscribe: (eventType: string, callback: (data: any) => void) => () => void
}

class WebSocketService {
  private ws: WebSocket | null = null
  private reconnectAttempts = 0
  private maxReconnectAttempts = 5
  private reconnectInterval = 5000
  private subscribers = new Map<string, Set<(data: any) => void>>()
  private store: any

  constructor() {
    // Initialize Zustand store
    this.store = create<WebSocketState>((set, get) => ({
      isConnected: false,
      lastMessage: null,
      connectionStatus: 'disconnected',
      
      connect: async () => await this.connect(),
      disconnect: () => this.disconnect(),
      subscribe: (eventType: string, callback: (data: any) => void) => this.subscribe(eventType, callback)
    }))
  }

  async connect() {
    if (this.ws?.readyState === WebSocket.OPEN) {
      console.log('🔌 WebSocket: Already connected, skipping...')
      return
    }

    console.log('🔌 WebSocket: Starting connection...')
    this.store.setState({ connectionStatus: 'connecting' })

    try {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      // FIXED: Use backend port instead of frontend port
      const host = window.location.hostname + ':8001'
      
      // Get fresh token from unified auth service first, then fallback to localStorage
      const { unifiedAdminAuth } = await import('./unified-admin-auth')
      let token = unifiedAdminAuth.getAccessToken() || localStorage.getItem('admin_auth_token')
      
      // Validate token before connecting
      if (!token) {
        console.error('❌ WebSocket: No token available from any source')
        this.store.setState({ connectionStatus: 'error' })
        return
      }
      
      console.log('🔌 WebSocket: Using token for connection:', token.substring(0, 20) + '...')
      
      // Check if token is expired and refresh if needed
      try {
        const payload = JSON.parse(atob(token.split('.')[1]))
        const now = Math.floor(Date.now() / 1000)
        
        console.log('🔍 WebSocket: Token validation:', {
          exp: payload.exp,
          now: now,
          expiresIn: payload.exp - now,
          needsRefresh: payload.exp < now + 300
        })
        
        // If token expires in less than 5 minutes, refresh it
        if (payload.exp < now + 300) {
          console.log('🔄 WebSocket: Token expiring soon, refreshing...')
          const refreshResult = await unifiedAdminAuth.refreshToken()
          if (refreshResult.token) {
            token = refreshResult.token
            console.log('✅ WebSocket: Token refreshed successfully')
          } else {
            console.error('❌ WebSocket: Failed to refresh token')
            this.store.setState({ connectionStatus: 'error' })
            return
          }
        } else {
          console.log('✅ WebSocket: Token is valid, proceeding with connection')
        }
      } catch (error) {
        console.error('❌ WebSocket: Token validation failed:', error)
        // Try to refresh anyway in development mode
        const isDevelopment = (import.meta as any).env?.VITE_NODE_ENV === 'development' || 
                             (import.meta as any).env?.DEV || 
                             window.location.hostname === 'localhost'
        
        if (isDevelopment) {
          try {
            console.log('🔧 WebSocket: Attempting development token refresh...')
            const refreshResult = await unifiedAdminAuth.refreshToken()
            if (refreshResult.token) {
              token = refreshResult.token
              console.log('✅ WebSocket: Token refreshed after validation failure')
            } else {
              console.error('❌ WebSocket: Token refresh failed')
              this.store.setState({ connectionStatus: 'error' })
              return
            }
          } catch (refreshError) {
            console.error('❌ WebSocket: Token refresh failed:', refreshError)
            this.store.setState({ connectionStatus: 'error' })
            return
          }
        } else {
          console.error('❌ WebSocket: Token validation failed in production mode')
          this.store.setState({ connectionStatus: 'error' })
          return
        }
      }
      
      const wsUrl = `${protocol}//${host}/api/admin/ws?token=${token}`
      console.log('🔌 WebSocket: Attempting connection to:', wsUrl.replace(/token=.*/, 'token=***'))
      
      this.ws = new WebSocket(wsUrl)

      this.ws.onopen = () => {
        console.log('✅ WebSocket connected successfully')
        this.reconnectAttempts = 0
        this.store.setState({
          isConnected: true,
          connectionStatus: 'connected'
        })
      }

      this.ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data)
          this.handleMessage(message)
          this.store.setState({ lastMessage: message })
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error)
        }
      }

      this.ws.onclose = (event) => {
        console.log('🔌 WebSocket disconnected:', {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
          reconnectAttempts: this.reconnectAttempts,
          maxAttempts: this.maxReconnectAttempts
        })
        
        this.store.setState({
          isConnected: false,
          connectionStatus: 'disconnected'
        })
        
        // Handle different close codes appropriately
        if (event.code === 1000) {
          console.log('✅ WebSocket: Normal closure, no reconnection needed')
        } else if (event.code === 1008) {
          console.error('❌ WebSocket: Authentication failed, not reconnecting')
          this.store.setState({ connectionStatus: 'error' })
        } else if (this.reconnectAttempts >= this.maxReconnectAttempts) {
          console.error('❌ WebSocket: Max reconnection attempts reached')
          this.store.setState({ connectionStatus: 'error' })
        } else {
          console.log('🔄 WebSocket: Scheduling reconnection for unexpected disconnection...')
          this.scheduleReconnect()
        }
      }

      this.ws.onerror = (error) => {
        console.error('❌ WebSocket error:', error)
        this.store.setState({ connectionStatus: 'error' })
      }
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error)
      this.store.setState({ connectionStatus: 'error' })
    }
  }

  disconnect() {
    if (this.ws) {
      this.ws.close(1000, 'Manual disconnect')
      this.ws = null
    }
    this.store.setState({
      isConnected: false,
      connectionStatus: 'disconnected'
    })
  }

  private scheduleReconnect() {
    this.reconnectAttempts++
    const delay = Math.min(this.reconnectInterval * Math.pow(2, this.reconnectAttempts - 1), 30000) // Max 30 seconds
    
    console.log(`🔄 WebSocket: Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`)
    this.store.setState({ connectionStatus: 'connecting' })
    
    setTimeout(async () => {
      try {
        await this.connect()
      } catch (error) {
        console.error('❌ WebSocket: Reconnection attempt failed:', error)
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this.scheduleReconnect()
        } else {
          console.error('❌ WebSocket: All reconnection attempts exhausted')
          this.store.setState({ connectionStatus: 'error' })
        }
      }
    }, delay)
  }

  private handleMessage(message: WebSocketMessage) {
    const subscribers = this.subscribers.get(message.type)
    if (subscribers) {
      subscribers.forEach(callback => {
        try {
          callback(message.data)
        } catch (error) {
          console.error('Error in WebSocket subscriber:', error)
        }
      })
    }
  }

  subscribe(eventType: string, callback: (data: any) => void): () => void {
    if (!this.subscribers.has(eventType)) {
      this.subscribers.set(eventType, new Set())
    }
    
    this.subscribers.get(eventType)!.add(callback)
    
    // Return unsubscribe function
    return () => {
      const subscribers = this.subscribers.get(eventType)
      if (subscribers) {
        subscribers.delete(callback)
        if (subscribers.size === 0) {
          this.subscribers.delete(eventType)
        }
      }
    }
  }

  // Convenience methods for common event types
  onSystemHealthUpdate(callback: (data: any) => void) {
    return this.subscribe('system_health_update', callback)
  }

  onGenerationUpdate(callback: (data: any) => void) {
    return this.subscribe('generation_update', callback)
  }

  onMLSExportUpdate(callback: (data: any) => void) {
    return this.subscribe('mls_export_update', callback)
  }

  onWorkerStatusUpdate(callback: (data: any) => void) {
    return this.subscribe('worker_status_update', callback)
  }

  onUserActivity(callback: (data: any) => void) {
    return this.subscribe('user_activity', callback)
  }

  onSecurityAlert(callback: (data: any) => void) {
    return this.subscribe('security_alert', callback)
  }

  onPaymentUpdate(callback: (data: any) => void) {
    return this.subscribe('payment_update', callback)
  }

  onAdminNotification(callback: (data: any) => void) {
    return this.subscribe('admin_notification', callback)
  }

  // Get the Zustand store for React components
  getStore() {
    return this.store
  }
}

// Create singleton instance
export const webSocketService = new WebSocketService()

// Export the store for React components
export const useWebSocketStore = webSocketService.getStore()

// React hook for easy WebSocket subscription
import { useEffect } from 'react'

export function useWebSocketSubscription(
  eventType: string,
  callback: (data: any) => void,
  deps: any[] = []
) {
  useEffect(() => {
    const unsubscribe = webSocketService.subscribe(eventType, callback)
    return unsubscribe
  }, deps)
}

// React hook for system health updates
export function useSystemHealthUpdates(callback: (data: any) => void) {
  useWebSocketSubscription('system_health_update', callback)
}

// React hook for generation updates
export function useGenerationUpdates(callback: (data: any) => void) {
  useWebSocketSubscription('generation_update', callback)
}

// React hook for MLS export updates
export function useMLSExportUpdates(callback: (data: any) => void) {
  useWebSocketSubscription('mls_export_update', callback)
}

// React hook for worker status updates
export function useWorkerStatusUpdates(callback: (data: any) => void) {
  useWebSocketSubscription('worker_status_update', callback)
}

// React hook for admin notifications
export function useAdminNotificationUpdates(callback: (data: any) => void) {
  useWebSocketSubscription('admin_notification', callback)
}

// WebSocket connection management will be handled by Layout component
// to avoid circular dependency issues